import React, { forwardRef } from 'react';
import * as RadixTooltipPrimitive from '@radix-ui/react-tooltip';
import { Portal } from '#components/portal/portal.tsx';
import type {
  GDSAlign,
  GDSCustomizableComponent,
  GDSResponsiveProp,
  GDSSide,
  GDSSpacingValues,
} from '../../types';
import { styled } from '../../theme';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { useTextDirectionality } from '../../theme/text-directionality-provider';

export interface GDSTooltipContentProps
  extends Omit<RadixTooltipPrimitive.TooltipContentProps, 'align' | 'side'>,
    GDSCustomizableComponent {
  align?: GDSResponsiveProp<GDSAlign>;
  padding?: GDSResponsiveProp<GDSSpacingValues>;
  side?: GDSResponsiveProp<GDSSide>;
}

const BaseTooltipContent = styled(RadixTooltipPrimitive.Content, {
  typography: '$bodyTwo',
  color: '$onSurfaceTextPrimary',
  borderRadius: '$medium',
  borderWidth: '$thin',
  borderStyle: 'solid',
  borderColor: '$onSurfaceBorderSubdued',
  backgroundColor: '$surfaceBackgroundPrimary',
  boxShadow: '$dropdown',
  zIndex: '$tooltip',
  '&[class*="GDS"]:focus, &[class*="GDS"]:active': {
    boxShadow: '$dropdown',
    outline: 'none',
    position: 'initial',
  },
});

const getPhysicalSide = (
  dir: 'ltr' | 'rtl',
  side?: GDSSide,
): Exclude<GDSSide, 'inlineStart' | 'inlineEnd'> | undefined => {
  if (!side) {
    return side;
  }

  if (!(side === 'inlineStart' || side === 'inlineEnd')) {
    return side;
  }

  if (dir === 'ltr') {
    if (side === 'inlineStart') {
      return 'left';
    }

    return 'right';
  }

  if (side === 'inlineStart') {
    return 'right';
  }

  return 'left';
};

export const TooltipContent = forwardRef<
  HTMLDivElement,
  GDSTooltipContentProps
>(
  (
    {
      align,
      avoidCollisions = true,
      children,
      className,
      collisionPadding,
      css,
      padding = '$half',
      side,
      sideOffset = 6,
      ...props
    },
    ref,
  ) => {
    const contentPadding = useResponsiveProp(padding);
    const responsiveSide = useResponsiveProp(side);
    const responsiveAlign = useResponsiveProp(align);

    const dir = useTextDirectionality();

    const physicalSide = getPhysicalSide(dir, responsiveSide);

    return (
      <Portal>
        <BaseTooltipContent
          align={responsiveAlign}
          avoidCollisions={avoidCollisions}
          className={['GDS-tooltip-content', className].join(' ')}
          collisionPadding={collisionPadding}
          css={{
            ...css,
            ...(padding && {
              padding: `${contentPadding}`,
            }),
          }}
          ref={ref}
          side={physicalSide}
          sideOffset={sideOffset}
          {...props}
        >
          {children}
        </BaseTooltipContent>
      </Portal>
    );
  },
);
